import React, { useCallback, useRef } from "react";
import { VideoPlayer } from "../ui";
import ContentWatermark from "./ContentWatermark";
import useVideoProtection from "../../hooks/useVideoProtection";
import "../../styles/VideoProtection.scss";

const noop = () => {};

/**
 * ProtectedVideoPlayer — `ui/VideoPlayer` with the content-protection stack on
 * it. Every video a student watches goes through this.
 *
 *   • the viewer's name + institute watermarked over the picture (drifting,
 *     with the time and a trace id), and kept there in full screen — the
 *     player's full screen is its box, never the bare video;
 *   • `useVideoProtection` on the box: right-click, save / view-source keys,
 *     screen-capture and recorder interception, downloader-extension probes,
 *     the src attribute hidden once the video has loaded.
 *
 * The player itself already refuses native controls, picture-in-picture,
 * remote playback and the iPhone's own full screen.
 *
 * Takes every `VideoPlayer` prop; `ref` is the <video> element, which a
 * watch-time tracker or a resume effect can hold.
 */
const ProtectedVideoPlayer = React.forwardRef(({ src, ...props }, ref) => {
  const rootRef = useRef(null);
  const videoRef = useRef(null);

  const setVideo = useCallback((el) => {
    videoRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  }, [ref]);

  useVideoProtection({
    containerRef: rootRef,
    onPause: useCallback(() => { videoRef.current?.pause(); }, []),
    onResume: noop,
    active: !!src,
  });

  return (
    <VideoPlayer
      ref={setVideo}
      rootRef={rootRef}
      src={src}
      overlay={<ContentWatermark videoMode fullscreenButton={false} />}
      {...props}
    />
  );
});

ProtectedVideoPlayer.displayName = "ProtectedVideoPlayer";
export default ProtectedVideoPlayer;
